import { Edge, Node } from "@xyflow/react";
import { useCallback, useRef } from "react";
import { useRecoilState } from "recoil";

import {
    ccEdgesAtom,
    ccNodesAtom,
    ccSelectedEdgeAtom,
    ccSelectedNodeAtom,
    SelectedEdgeData,
    SelectedNodeData,
} from "../atoms/diagramAtom";
import { NodeType } from "../types/enums";
import { NodeFormData } from "../utils/nodeDefaults";

/**
 * Hook for diagram state actions (delete, update nodes/edges)
 * Use this in drawers and other components that need to modify diagram state
 */
export const useDiagramActions = () => {
    const [nodes, setNodes] = useRecoilState(ccNodesAtom);
    const [edges, setEdges] = useRecoilState(ccEdgesAtom);

    // Use refs to avoid stale closure issues in callbacks
    const nodesRef = useRef<Node[]>(nodes);
    const edgesRef = useRef<Edge[]>(edges);
    nodesRef.current = nodes;
    edgesRef.current = edges;

    const deleteNode = useCallback(
        (nodeId: string) => {
            // Read from refs to get current state
            const currentEdges = edgesRef.current;
            const currentNodes = nodesRef.current;

            // Find connected methods node if deleting a Start node
            const connectedEdge = currentEdges.find((edge) => edge.source === nodeId);
            const connectedMethodsNode = connectedEdge
                ? currentNodes.find((n) => n.id === connectedEdge.target && n.type === NodeType.Methods)
                : null;

            // Collect all node IDs to remove
            const nodeIdsToRemove = connectedMethodsNode ? [nodeId, connectedMethodsNode.id] : [nodeId];

            setNodes((prevNodes) => prevNodes.filter((n) => !nodeIdsToRemove.includes(n.id)));
            setEdges((prevEdges) =>
                prevEdges.filter((e) => !nodeIdsToRemove.includes(e.source) && !nodeIdsToRemove.includes(e.target))
            );
        },
        [setNodes, setEdges]
    );

    const updateNodeFormData = useCallback(
        (nodeId: string, formData: NodeFormData) => {
            setNodes((nodes) =>
                nodes.map((node) => (node.id === nodeId ? { ...node, data: { ...node.data, formData } } : node))
            );
        },
        [setNodes]
    );

    const deleteEdge = useCallback(
        (edgeId: string) => {
            setEdges((edges) => edges.filter((e) => e.id !== edgeId));
        },
        [setEdges]
    );

    const updateEdgeData = useCallback(
        (edgeId: string, data: Record<string, unknown>) => {
            setEdges((edges) => edges.map((e) => (e.id === edgeId ? { ...e, data: { ...e.data, ...data } } : e)));
        },
        [setEdges]
    );

    return {
        deleteNode,
        updateNodeFormData,
        deleteEdge,
        updateEdgeData,
    };
};

/**
 * Hook for selected node state and actions
 */
export const useSelectedNode = () => {
    const [selectedNode, setSelectedNode] = useRecoilState(ccSelectedNodeAtom);
    const { deleteNode, updateNodeFormData } = useDiagramActions();

    const selectNode = useCallback(
        (node: SelectedNodeData | null) => {
            setSelectedNode(node);
        },
        [setSelectedNode]
    );

    const deleteSelectedNode = useCallback(() => {
        if (selectedNode) {
            deleteNode(selectedNode.id);
            setSelectedNode(null);
        }
    }, [selectedNode, deleteNode, setSelectedNode]);

    const updateSelectedNodeFormData = useCallback(
        (formData: NodeFormData) => {
            if (selectedNode) {
                // Merge with existing formData to preserve non-form fields (e.g. beType on startNode)
                const merged = { ...selectedNode.formData, ...formData };
                updateNodeFormData(selectedNode.id, merged);
                // Also update local selected node state
                setSelectedNode((prev) => (prev ? { ...prev, formData: merged } : null));
            }
        },
        [selectedNode, updateNodeFormData, setSelectedNode]
    );

    return {
        selectedNode,
        setSelectedNode: selectNode,
        deleteSelectedNode,
        updateNodeFormData: updateSelectedNodeFormData,
    };
};

/**
 * Hook for selected edge state and actions
 */
export const useSelectedEdge = () => {
    const [selectedEdge, setSelectedEdge] = useRecoilState(ccSelectedEdgeAtom);
    const { deleteEdge, updateEdgeData } = useDiagramActions();

    const selectEdge = useCallback(
        (edge: SelectedEdgeData | null) => {
            setSelectedEdge(edge);
        },
        [setSelectedEdge]
    );

    const deleteSelectedEdge = useCallback(() => {
        if (selectedEdge) {
            deleteEdge(selectedEdge.id);
            setSelectedEdge(null);
        }
    }, [selectedEdge, deleteEdge, setSelectedEdge]);

    const updateSelectedEdgeData = useCallback(
        (data: Record<string, unknown>) => {
            if (selectedEdge) {
                updateEdgeData(selectedEdge.id, data);
                setSelectedEdge((prev) => (prev ? { ...prev, data: { ...prev.data, ...data } } : null));
            }
        },
        [selectedEdge, updateEdgeData, setSelectedEdge]
    );

    return {
        selectedEdge,
        setSelectedEdge: selectEdge,
        deleteSelectedEdge,
        updateEdgeData: updateSelectedEdgeData,
    };
};
